import { cloneDeep } from 'lodash';
import {
  ERROR_TEXT_EMPTY_REQUIRED_FIELD,
  ERROR_TEXT_CHECKED_FIELD_NO_TEXT,
  ERROR_TEXT_EMPTY_FORM_NAME,
  ERROR_TEXT_NO_DEFAULT_VALUE,
  ERROR_TEXT_NO_DEGREE_OFFERING,
  ERROR_TEXT_NO_GDPR_SETTING,
  ERROR_TEXT_NO_PROGRAM_OF_STUDY,
} from 'langs/en/errors';
import ConditionalityGraph from 'utils/conditionality.graph';
import { standardFieldNameToTaxiFieldName } from 'utils/coercionUtils';
import {
  ALL_BRANCHES,
  CONDITION_RENDER_IF_NON_GDPR,
  CONDITION_RENDER_IF_GDPR,
} from './conditionalConstants';
import {
  FIELD_TYPES,
  FIELD_NAMES,
  FLAGS_NONREMOVABLE,
  DISCLAIMER_LOCATION_TYPES,
  ID_PREFIX,
  OPTIN_RADIO_DEFAULTS,
  OPTIN_FORMATS,
  PREFIX_SCREEN,
  PUBLISHED_STATUS,
  REQUIRED_FIELD_PARAMETERS,
  FORM_STYLE,
  GDPR_SETTING,
  CREDENTIAL_TYPES,
  DEFAULT_IMPLIED_PHONE_CONSENT_KEY,
  DEFAULT_IMPLIED_EMAIL_CONSENT_KEY,
  DEFAULT_IMPLIED_PHONE_OPT_IN_KEY,
  DEFAULT_IMPLIED_EMAIL_OPT_IN_KEY,
  DEFAULT_OPTIN_CHECKBOX_TEXT,
  DEFAULT_OPTIN_DISCLAIMER,
  DEFAULT_IMPLIED_CONSENT_DISCLAIMER_VALUE,
  DEFAULT_IMPLIED_CONSENT_DISCLAIMER_OVERRIDE,
  DEFAULT_PHONE_HELP_TEXT,
  DEFAULT_LEAD_SHARE_OPT_IN_CHECKBOX_TEXT,
} from './constants';

export const initialFieldIds = {
  firstName: ID_PREFIX.concat('a'),
  lastName: ID_PREFIX.concat('b'),
  email: ID_PREFIX.concat('c'),
  consent: ID_PREFIX.concat('d'),
  phone: ID_PREFIX.concat('e'),
  sms: ID_PREFIX.concat('f'),
};

export const initialScreenId = ID_PREFIX.concat('a');

function createConsentChannelDefaultFieldState() {
  return {
    channelData: {
      email: {
        include: true,
        text: '',
      },
      phone: {
        include: false,
        text: '',
      },
      sms: {
        include: false,
        text: '',
      },
    },
  };
}

export function createDefaultFieldStateForType(
  fieldType,
  gdprSetting = GDPR_SETTING.NEVER,
  credentialType = CREDENTIAL_TYPES.DEGREE,
) {
  switch (fieldType) {
    case FIELD_TYPES.DEGREE_OFFERING:
      return {
        degreeOfferingConfig: {
          programId: '',
          degreeOffering: '',
        },
      };
    case FIELD_TYPES.DROPDOWN:
      return {
        dropdownConfig: {
          defaultOption: '',
        },
      };
    case FIELD_TYPES.PICKLIST_VALUE_OPT_IN:
    case FIELD_TYPES.BOOLEAN_OPT_IN:
      return {
        optInData: {
          checkboxText: DEFAULT_OPTIN_CHECKBOX_TEXT,
          disclaimer: DEFAULT_OPTIN_DISCLAIMER,
          hasCheckbox: true,
          hasDisclaimer: true,
          optInValue: '',
          format: [GDPR_SETTING.ALWAYS, GDPR_SETTING.ALWAYS_IMPLIED].includes(gdprSetting) ? OPTIN_FORMATS.RADIO : OPTIN_FORMATS.CHECKBOX,
          defaultChecked: false,
          defaultRadio: OPTIN_RADIO_DEFAULTS.NONE,
        },
      };
    case FIELD_TYPES.NON_GDPR_CONSENT:
      return {
        optInData: {
          checkboxText: '',
          disclaimer: '',
          hasCheckbox: true,
          hasDisclaimer: true,
          optInValue: '',
          format: [GDPR_SETTING.ALWAYS, GDPR_SETTING.ALWAYS_IMPLIED].includes(gdprSetting) ? OPTIN_FORMATS.RADIO : OPTIN_FORMATS.CHECKBOX,
          defaultChecked: true,
          defaultRadio: OPTIN_RADIO_DEFAULTS.NONE,
        },
        condition: CONDITION_RENDER_IF_NON_GDPR,
      };
    case FIELD_TYPES.PHONE: {
      const isShortCourse = credentialType === CREDENTIAL_TYPES.SHORT_COURSE;
      const phoneKey = isShortCourse ? DEFAULT_IMPLIED_PHONE_OPT_IN_KEY : DEFAULT_IMPLIED_PHONE_CONSENT_KEY;
      return {
        phoneConfig: {
          impliedConsentData: [
            {
              key: phoneKey,
              userDoesConsent: false,
              userDoesNotConsent: true,
            },
          ],
          includeImpliedConsent: [GDPR_SETTING.ALWAYS_IMPLIED, GDPR_SETTING.GEO_IMPLIED, GDPR_SETTING.PASS_CONSENT_FOR_ALL_USERS].includes(gdprSetting),
        },
      };
    }
    case FIELD_TYPES.EMAIL: {
      const isShortCourse = credentialType === CREDENTIAL_TYPES.SHORT_COURSE;
      const emailKey = isShortCourse ? DEFAULT_IMPLIED_EMAIL_OPT_IN_KEY : DEFAULT_IMPLIED_EMAIL_CONSENT_KEY;
      return {
        emailConfig: {
          impliedConsentData: [
            {
              key: emailKey,
              userDoesConsent: false,
              userDoesNotConsent: true,
            },
          ],
          includeImpliedConsent: [GDPR_SETTING.ALWAYS_IMPLIED, GDPR_SETTING.GEO_IMPLIED, GDPR_SETTING.PASS_CONSENT_FOR_ALL_USERS].includes(gdprSetting),
        },
      };
    }
    case FIELD_TYPES.GDPR_CONSENT:
      return {
        gdprConsentData: {
          retailOptIn: {
            text: '',
            ...createConsentChannelDefaultFieldState(),
          },
          leadShareOptIn: {
            include: false,
            text: '',
            leadShareValue: '',
            ...createConsentChannelDefaultFieldState(),
          },
          disclaimer: {
            include: false,
            text: '',
          },
        },
        condition: CONDITION_RENDER_IF_GDPR,
      };
    default: {
      return {
        value: '',
      };
    }
  }
}

export function createDefaultUIStateForType(fieldType, isHidden = false) {
  switch (fieldType) {
    case FIELD_TYPES.DEGREE_OFFERING: {
      const retval = {
        degreeOfferingConfig: {
          programId: {
            required: true,
          },
          degreeOffering: {},
        },
      };

      if (isHidden) {
        retval.degreeOfferingConfig.programId = {
          ...retval.degreeOfferingConfig.programId,
          isValid: false,
          errorDisplay: ERROR_TEXT_EMPTY_REQUIRED_FIELD,
          className: 'is-invalid',
        };
      }
      return retval;
    }
    case FIELD_TYPES.DROPDOWN: {
      const retval = {
        dropdownConfig: {
          options: {},
        },
      };

      if (isHidden) {
        retval.dropdownConfig.defaultOption = {
          required: true,
          isValid: false,
          errorDisplay: ERROR_TEXT_NO_DEFAULT_VALUE,
          className: 'is-invalid',
        };
      }

      return retval;
    }
    case FIELD_TYPES.MULTIPICKLIST: {
      const retval = {
        multipicklistConfig: {
          options: {},
        },
      };

      if (isHidden) {
        retval.multipicklistConfig.defaultOption = {
          required: true,
          isValid: false,
          errorDisplay: ERROR_TEXT_NO_DEFAULT_VALUE,
          className: 'is-invalid',
        };
      }

      return retval;
    }
    case FIELD_TYPES.OPTIN:
    case FIELD_TYPES.PICKLIST_VALUE_OPT_IN:
    case FIELD_TYPES.BOOLEAN_OPT_IN:
    case FIELD_TYPES.NON_GDPR_CONSENT:
      return {
        optInData: {
          checkboxText: {},
          disclaimer: {},
          hasCheckbox: {},
          hasDisclaimer: {},
          optInValue: {
            required: true,
            isValid: false,
            errorDisplay: ERROR_TEXT_EMPTY_REQUIRED_FIELD,
            className: 'is-invalid',
          },
          format: {},
          defaultChecked: {},
          defaultRadio: {},
        },
      };
    case FIELD_TYPES.GDPR_CONSENT:
      return {
        gdprConsentData: {
          retailOptIn: {
            text: {
              isValid: false,
              errorDisplay: ERROR_TEXT_EMPTY_REQUIRED_FIELD,
              className: 'is-invalid',
            },
            channelData: {
              email: {
                include: {},
                text: {
                  isValid: false,
                  errorDisplay: ERROR_TEXT_CHECKED_FIELD_NO_TEXT,
                  className: 'is-invalid',
                },
              },
              phone: {
                include: {},
                text: {},
              },
              sms: {
                include: {},
                text: {},
              },
            },
          },
          leadShareOptIn: {
            include: {},
            text: {},
            leadShareValue: {},
            channelData: {
              email: {
                include: {},
                text: {},
              },
              phone: {
                include: {},
                text: {},
              },
              sms: {
                include: {},
                text: {},
              },
            },
          },
          disclaimer: {
            include: {},
            text: {},
          },
        },
      };
    case FIELD_TYPES.RADIO_BUTTON: {
      const retval = {
        radioButtonConfig: {
          defaultOption: {},
          options: {},
          layout: {},
        },
      };
      if (isHidden) {
        retval.radioButtonConfig.defaultOption = {
          required: true,
          isValid: false,
          errorDisplay: ERROR_TEXT_NO_DEFAULT_VALUE,
          className: 'is-invalid',
        };
      }
      return retval;
    }
    default: {
      const retval = {
        value: {},
      };
      if (isHidden) {
        retval.value = {
          required: true,
          isValid: false,
          errorDisplay: ERROR_TEXT_NO_DEFAULT_VALUE,
          className: 'is-invalid',
        };
      }
      return retval;
    }
  }
}

export function createDefaultInvalidFormDataForType(
  fieldType,
  isHidden = false,
) {
  switch (fieldType) {
    case FIELD_TYPES.DEGREE_OFFERING:
      return {
        degreeOfferingConfig: {
          programId: {
            error: {
              visible: false,
              text: ERROR_TEXT_EMPTY_REQUIRED_FIELD,
            },
          },
        },
      };
    case FIELD_TYPES.GDPR_CONSENT:
      return {
        gdprConsentData: {
          retailOptIn: {
            text: {
              error: {
                visible: false,
                text: ERROR_TEXT_EMPTY_REQUIRED_FIELD,
              },
            },
            channelData: {
              email: {
                text: {
                  error: {
                    visible: false,
                    text: ERROR_TEXT_CHECKED_FIELD_NO_TEXT,
                  },
                },
              },
            },
          },
        },
      };
    case FIELD_TYPES.OPTIN:
    case FIELD_TYPES.NON_GDPR_CONSENT:
      return {
        optInData: {
          disclaimer: {
            error: {
              visible: false,
              text: ERROR_TEXT_CHECKED_FIELD_NO_TEXT,
            },
          },
          checkboxText: {
            error: {
              visible: false,
              text: ERROR_TEXT_CHECKED_FIELD_NO_TEXT,
            },
          },
          optInValue: {
            error: {
              visible: false,
              text: ERROR_TEXT_EMPTY_REQUIRED_FIELD,
            },
          },
        },
      };
    case FIELD_TYPES.PICKLIST_VALUE_OPT_IN:
    case FIELD_TYPES.BOOLEAN_OPT_IN:
      return {
        optInData: {
          optInValue: {
            error: {
              visible: false,
              text: ERROR_TEXT_EMPTY_REQUIRED_FIELD,
            },
          },
        },
      };
    case FIELD_TYPES.DROPDOWN:
      return isHidden ? {
        dropdownConfig: {
          defaultOption: {
            error: {
              visible: false,
              text: ERROR_TEXT_NO_DEFAULT_VALUE,
            },
          },
        },
      } : {};
    case FIELD_TYPES.RADIO_BUTTON:
      return isHidden ? {
        radioButtonConfig: {
          defaultOption: {
            error: {
              visible: false,
              text: ERROR_TEXT_NO_DEFAULT_VALUE,
            },
          },
        },
      } : {};
    default:
      return isHidden ? {
        value: {
          error: {
            visible: false,
            text: ERROR_TEXT_NO_DEFAULT_VALUE,
          },
        },
      } : {};
  }
}

export function createDefaultOverrideStateForType(
  fieldType,
  consentStyle,
  fieldName,
) {
  if (![GDPR_SETTING.GEO_IMPLIED, GDPR_SETTING.PASS_CONSENT_FOR_ALL_USERS].includes(consentStyle)) {
    return {};
  }

  if (
    consentStyle === GDPR_SETTING.GEO_IMPLIED &&
    fieldName === FIELD_NAMES.SMS_OPT_IN_MARKETING
  ) {
    return {
      gdpr: {
        editors: {
          defaultOption: 'false',
        },
        value: {
          defaultOption: 'false',
        },
      },
    };
  }

  switch (fieldType) {
    case FIELD_TYPES.EMAIL:
      return {
        gdpr: {
          editors: {
            emailConfig: {
              includeImpliedConsent: [GDPR_SETTING.GEO_IMPLIED, GDPR_SETTING.PASS_CONSENT_FOR_ALL_USERS].includes(consentStyle),
            },
          },
        },
      };
    case FIELD_TYPES.PHONE:
      return {
        gdpr: {
          label: '',
          helpText: { value: '' },
          required: false,
          hidden: false,
          editors: {
            phoneConfig: {
              includeImpliedConsent: [GDPR_SETTING.GEO_IMPLIED, GDPR_SETTING.PASS_CONSENT_FOR_ALL_USERS].includes(consentStyle),
            },
          },
        },
      };
    case FIELD_TYPES.PICKLIST_VALUE_OPT_IN:
    case FIELD_TYPES.BOOLEAN_OPT_IN:
      return {
        gdpr: {
          editors: {
            optInData: {
              format: OPTIN_FORMATS.RADIO,
              defaultRadio: OPTIN_RADIO_DEFAULTS.NONE,
            },
          },
        },
      };
    default:
      return {};
  }
}

export function createDefaultOverrideUIStateForType(
  fieldType,
  consentStyle = GDPR_SETTING.NEVER,
) {
  if (![GDPR_SETTING.GEO_IMPLIED, GDPR_SETTING.PASS_CONSENT_FOR_ALL_USERS].includes(consentStyle)) {
    return {};
  }
  switch (fieldType) {
    case FIELD_TYPES.PHONE:
      return {
        gdpr: {
          label: '',
          helpText: {
            value: {
              isValid: false,
              required: true,
              errorDisplay: ERROR_TEXT_EMPTY_REQUIRED_FIELD,
              className: 'is-invalid',
            },
          },
          rules: {},
        },
      };
    case FIELD_TYPES.PICKLIST_VALUE_OPT_IN:
    case FIELD_TYPES.BOOLEAN_OPT_IN:
      return {
        gdpr: {
          editors: {
            optInData: {
              checkboxText: {},
              disclaimer: {},
              format: 'radio',
              defaultRadio: 'none',
            },
          },
        },
      };
    default:
      return {};
  }
}

export function createDefaultInvalidOverrideDataForType(
  fieldType,
  consentStyle = GDPR_SETTING.NEVER,
) {
  // Only return error structures for GEO_IMPLIED. For PASS_CONSENT_FOR_ALL_USERS,
  // we populate default values so there should be no errors.
  if (consentStyle !== GDPR_SETTING.GEO_IMPLIED) return {};
  switch (fieldType) {
    case FIELD_TYPES.PHONE:
      return {
        gdpr: {
          helpText: {
            value: {
              error: {
                visible: false,
                text: ERROR_TEXT_EMPTY_REQUIRED_FIELD,
              },
            },
          },
        },
      };
    default:
      return {};
  }
}

export function createDefaultFieldStateFromFieldData(
  id,
  salesForceFieldName = '',
  type = FIELD_TYPES.TEXT,
  label = '',
  gdprSetting = GDPR_SETTING.NEVER,
  credentialType = CREDENTIAL_TYPES.DEGREE,
) {
  const editors = createDefaultFieldStateForType(type, gdprSetting, credentialType);
  return {
    id,
    editors,
    new: true,
    hidden: false,
    name: salesForceFieldName,
    required: true,
    type,
    label,
    flags: [],
    helpText: {
      hasHelpText: false,
      value: '',
    },
  };
}

function createDefaultFieldStateForNonremovableField(
  id,
  salesForceFieldName,
  type,
  label,
  credentialType = CREDENTIAL_TYPES.DEGREE,
) {
  const defaultState = createDefaultFieldStateFromFieldData(
    id,
    salesForceFieldName,
    type,
    label,
    GDPR_SETTING.NEVER,
    credentialType,
  );
  defaultState.flags = [FLAGS_NONREMOVABLE];
  return defaultState;
}

function createDefaultUIStateForRequiredField() {
  return {
    label: {
      required: true,
    },
    name: {
      required: true,
    },
    type: {
      required: true,
    },
    editors: {},
    helpText: { hasHelpText: {}, value: {} },
  };
}
const initialFormStateDegrees = {
  form: {
    name: '',
    degreeOffering: '',
    programId: -1,
    publishedStatus: PUBLISHED_STATUS.UNPUBLISHED,
    groupingName: '',
    groupingId: -1,
    formStyle: FORM_STYLE.STANDARD,
    title: '',
    introText: '',
    introSubText: '',
    thankYouURL: '',
    leadSource: '',
    phone: '',
    sms: '',
    splashCreative: '',
    formMetadata: {
      areAllFieldsOptional: false,
      isFormJSONEditable: false,
      experimentalTrialAttributes: {},
      formAttributesConfig: {
        impliedConsent: true,
      },
    },
    syntheticContentEntities: {
      gdprSetting: GDPR_SETTING.GEO_IMPLIED,
      disclaimers: {
        [DISCLAIMER_LOCATION_TYPES.body]: {
          show: true,
          value: DEFAULT_IMPLIED_CONSENT_DISCLAIMER_VALUE,
          override: DEFAULT_IMPLIED_CONSENT_DISCLAIMER_OVERRIDE,
        },
        [DISCLAIMER_LOCATION_TYPES.footer]: {
          show: false,
          value: '',
          override: '',
        },
      },
    },
    fieldConditionalityInfo: { branchingFields: {}, dehydratedGraph: {} },
    thankYouRoutingFields: {},
    fields: {
      [initialFieldIds.firstName]: createDefaultFieldStateForNonremovableField(
        initialFieldIds.firstName,
        'first_name',
        FIELD_TYPES.TEXT,
        'First Name',
      ),
      [initialFieldIds.lastName]: createDefaultFieldStateForNonremovableField(
        initialFieldIds.lastName,
        'last_name',
        FIELD_TYPES.TEXT,
        'Last Name',
      ),
      [initialFieldIds.email]: {
        ...createDefaultFieldStateForNonremovableField(
          initialFieldIds.email,
          'email',
          FIELD_TYPES.EMAIL,
          'Email',
          CREDENTIAL_TYPES.DEGREE,
        ),
        overrides: {
          gdpr: {
            editors: {
              emailConfig: {
                includeImpliedConsent: true,
              },
            },
            __typename: 'FieldOverride',
          },
        },
      },
      [initialFieldIds.phone]: {
        ...createDefaultFieldStateFromFieldData(
          initialFieldIds.phone,
          FIELD_NAMES.PHONE,
          FIELD_TYPES.PHONE,
          'Phone',
          GDPR_SETTING.NEVER,
          CREDENTIAL_TYPES.DEGREE,
        ),
        helpText: {
          hasHelpText: false,
          value: '',
        },
        editors: {
          phoneConfig: {
            includeImpliedConsent: true,
          },
        },
        overrides: {
          gdpr: {
            label: 'Phone (Optional)',
            helpText: {
              value: DEFAULT_PHONE_HELP_TEXT,
            },
            required: false,
            hidden: false,
            editors: {
              phoneConfig: {
                includeImpliedConsent: true,
              },
            },
            __typename: 'FieldOverride',
          },
        },
        required: true,
      },
      [initialFieldIds.sms]: {
        ...createDefaultFieldStateFromFieldData(
          initialFieldIds.sms,
          FIELD_NAMES.SMS_OPT_IN_MARKETING,
          FIELD_TYPES.RADIO_BUTTON,
          'SMS Opt-In',
        ),
        hidden: true,
        required: false,
        editors: {
          value: 'Yes',
          radioButtonConfig: {
            defaultOption: 'true',
            optionsOrder: ['true', 'false'],
            layout: 'vertical',
            options: {
              true: { label: 'Yes', value: 'true', shouldDisplay: true },
              false: { label: 'No', value: 'false', shouldDisplay: true },
            },
          },
        },
      },
      [initialFieldIds.consent]: {
        ...createDefaultFieldStateFromFieldData(
          initialFieldIds.consent,
          FIELD_NAMES.LEAD_SHARE_OPT_IN,
          FIELD_TYPES.PICKLIST_VALUE_OPT_IN,
          'Lead Share Opt In',
        ),
        editors: {
          ...createDefaultFieldStateForType(FIELD_TYPES.PICKLIST_VALUE_OPT_IN),
        },
        optInData: {
          ...createDefaultFieldStateForType(FIELD_TYPES.PICKLIST_VALUE_OPT_IN)
            .optInData,
        },
        overrides: {
          gdpr: {
            editors: {
              optInData: {
                format: 'checkbox',
                disclaimer: DEFAULT_OPTIN_DISCLAIMER,
                checkboxText: DEFAULT_LEAD_SHARE_OPT_IN_CHECKBOX_TEXT,
                defaultChecked: false,
              },
            },
          },
        },
      },
    },
    screens: [
      {
        id: initialScreenId,
      },
    ],
    fieldOrder: [
      `${PREFIX_SCREEN}${initialScreenId}`,
      initialFieldIds.firstName,
      initialFieldIds.lastName,
      initialFieldIds.email,
      initialFieldIds.phone,
      initialFieldIds.sms,
      initialFieldIds.consent,
    ],
  },
  meta: {
    availableCredentialTypes: [],
    availablePrograms: [],
    availableProgramsOfStudy: [],
    fieldNameDropdownOpts: {},
    isDirty: true,
    loadInProgress: false,
    saveInProgress: false,
    sfFieldData: {},
    supportsDegreeOffering: false,
    uiState: {
      fieldOrderCollapsedState: Array(8).fill(false),
      name: {
        required: true,
        errorDisplay: ERROR_TEXT_EMPTY_FORM_NAME,
      },
      syntheticContentEntities: {
        gdprSetting: {
          required: true,
        },
        disclaimers: {
          [DISCLAIMER_LOCATION_TYPES.body]: {
            show: {},
            value: {},
            override: {
              isValid: true,
              errorDisplay: '',
              className: '',
            },
          },
          [DISCLAIMER_LOCATION_TYPES.footer]: {
            show: {},
            value: {
              required: true,
              errorDisplay: ERROR_TEXT_CHECKED_FIELD_NO_TEXT,
            },
            override: {
              isValid: true,
              errorDisplay: '',
              className: '',
            },
          },
        },
      },
      visibleBranches: ALL_BRANCHES,
      degreeOffering: {
        required: true,
        errorDisplay: ERROR_TEXT_NO_DEGREE_OFFERING,
      },
      programOfStudy: {
        required: true,
        errorDisplay: ERROR_TEXT_NO_PROGRAM_OF_STUDY,
      },
      gdprSetting: {
        required: true,
        errorDisplay: ERROR_TEXT_NO_GDPR_SETTING,
      },
      fields: {
        [initialFieldIds.firstName]: createDefaultUIStateForRequiredField(),
        [initialFieldIds.lastName]: createDefaultUIStateForRequiredField(),
        [initialFieldIds.email]: createDefaultUIStateForRequiredField(),
        [initialFieldIds.phone]: createDefaultUIStateForRequiredField(),
        [initialFieldIds.sms]: {
          ...createDefaultUIStateForRequiredField(),
          editors: {
            radioButtonConfig: {
              value: {
                required: true,
                isValid: true,
                errorDisplay: '',
                className: '',
              },
            },
          },
        },
        [initialFieldIds.consent]: {
          ...createDefaultUIStateForRequiredField(),
          editors: {
            ...createDefaultUIStateForType(FIELD_TYPES.PICKLIST_VALUE_OPT_IN),
          },
        },
      },
    },
    invalidFormData: {
      name: {
        error: {
          visible: false,
          text: ERROR_TEXT_EMPTY_FORM_NAME,
        },
      },
      fields: {
        [initialFieldIds.consent]: {
          editors: {
            ...createDefaultInvalidFormDataForType(
              FIELD_TYPES.PICKLIST_VALUE_OPT_IN,
            ),
          },
        },
      },
    },
  },
};
const initialFormStateShortCourses = {
  // eslint-disable-line
  form: {
    name: '',
    degreeOffering: '',
    programId: -1,
    publishedStatus: PUBLISHED_STATUS.UNPUBLISHED,
    groupingName: '',
    groupingId: -1,
    formStyle: FORM_STYLE.STANDARD,
    title: '',
    introText: '',
    introSubText: '',
    thankYouURL: '',
    leadSource: '',
    splashCreative: '',
    formMetadata: {
      areAllFieldsOptional: false,
      isFormJSONEditable: false,
      experimentalTrialAttributes: {},
      formAttributesConfig: {
        impliedConsent: true,
        send_consent_for_all_users: true,
      },
    },
    syntheticContentEntities: {
      gdprSetting: GDPR_SETTING.PASS_CONSENT_FOR_ALL_USERS,
      disclaimers: {
        [DISCLAIMER_LOCATION_TYPES.body]: {
          show: false,
          value: '',
          override: '',
        },
        [DISCLAIMER_LOCATION_TYPES.footer]: {
          show: true,
          value: '',
          override: '',
        },
      },
    },
    fieldConditionalityInfo: { branchingFields: {}, dehydratedGraph: {} },
    thankYouRoutingFields: {},
    fields: {
      [initialFieldIds.firstName]: createDefaultFieldStateForNonremovableField(
        initialFieldIds.firstName,
        'first_name',
        FIELD_TYPES.TEXT,
        'First Name',
      ),
      [initialFieldIds.lastName]: createDefaultFieldStateForNonremovableField(
        initialFieldIds.lastName,
        'last_name',
        FIELD_TYPES.TEXT,
        'Last Name',
      ),
      [initialFieldIds.email]: createDefaultFieldStateForNonremovableField(
        initialFieldIds.email,
        'email',
        FIELD_TYPES.EMAIL,
        'Email',
        CREDENTIAL_TYPES.SHORT_COURSE,
      ),
      [initialFieldIds.consent]: {
        ...createDefaultFieldStateFromFieldData(
          initialFieldIds.consent,
          FIELD_NAMES.CONSENT_GDPR,
          FIELD_TYPES.GDPR_CONSENT,
          'GDPR Marketing Consent 🇪🇺',
        ),
        editors: {
          ...createDefaultFieldStateForType(FIELD_TYPES.GDPR_CONSENT),
        },
      },
    },
    screens: [
      {
        id: initialScreenId,
      },
    ],
    fieldOrder: [
      `${PREFIX_SCREEN}${initialScreenId}`,
      initialFieldIds.firstName,
      initialFieldIds.lastName,
      initialFieldIds.email,
      initialFieldIds.consent,
    ],
  },
  meta: {
    availableCredentialTypes: [],
    availablePrograms: [],
    availableProgramsOfStudy: [],
    fieldNameDropdownOpts: {},
    isDirty: true,
    loadInProgress: false,
    saveInProgress: false,
    sfFieldData: {},
    supportsDegreeOffering: false,
    uiState: {
      fieldOrderCollapsedState: Array(6).fill(false),
      name: {
        required: true,
        errorDisplay: ERROR_TEXT_EMPTY_FORM_NAME,
      },
      syntheticContentEntities: {
        gdprSetting: {
          required: true,
        },
        disclaimers: {
          [DISCLAIMER_LOCATION_TYPES.body]: {
            show: {},
            value: {},
            override: {
              isValid: true,
              errorDisplay: '',
              className: '',
            },
          },
          [DISCLAIMER_LOCATION_TYPES.footer]: {
            show: {},
            value: {
              required: true,
              errorDisplay: ERROR_TEXT_CHECKED_FIELD_NO_TEXT,
            },
            override: {
              isValid: true,
              errorDisplay: '',
              className: '',
            },
          },
        },
      },
      visibleBranches: ALL_BRANCHES,
      degreeOffering: {
        required: true,
        errorDisplay: ERROR_TEXT_NO_DEGREE_OFFERING,
      },
      programOfStudy: {
        required: true,
        errorDisplay: ERROR_TEXT_NO_PROGRAM_OF_STUDY,
      },
      gdprSetting: {
        required: true,
        errorDisplay: ERROR_TEXT_NO_GDPR_SETTING,
      },
      fields: {
        [initialFieldIds.firstName]: createDefaultUIStateForRequiredField(),
        [initialFieldIds.lastName]: createDefaultUIStateForRequiredField(),
        [initialFieldIds.email]: createDefaultUIStateForRequiredField(),
        [initialFieldIds.consent]: {
          ...createDefaultUIStateForRequiredField(),
          editors: {
            ...createDefaultUIStateForType(FIELD_TYPES.GDPR_CONSENT),
          },
        },
      },
    },
    invalidFormData: {
      name: {
        error: {
          visible: false,
          text: ERROR_TEXT_EMPTY_FORM_NAME,
        },
      },
      syntheticContentEntities: {
        disclaimers: {
          [DISCLAIMER_LOCATION_TYPES.footer]: {
            value: {
              error: {
                visible: false,
                text: ERROR_TEXT_CHECKED_FIELD_NO_TEXT,
              },
            },
          },
        },
      },
      fields: {
        [initialFieldIds.consent]: {
          editors: {
            ...createDefaultInvalidFormDataForType(FIELD_TYPES.GDPR_CONSENT),
          },
        },
      },
    },
  },
};

export function createInitialFormBuilderState(
  credentialType = CREDENTIAL_TYPES.DEGREE,
) {
  const initialState = cloneDeep(
    credentialType === CREDENTIAL_TYPES.SHORT_COURSE ?
      initialFormStateShortCourses :
      initialFormStateDegrees,
  );
  const graph = new ConditionalityGraph({});
  const fields = Object.values(initialState.form.fields).map((field) => ({
    id: field.id,
    condition: field.editors.condition,
    name: field.name,
  }));
  const stateToFieldIdMap = fields.reduce((memo, field) => {
    memo[`state.${standardFieldNameToTaxiFieldName(field.name)}`] = field.id; // eslint-disable-line no-param-reassign
    return memo;
  }, {});
  fields.forEach((field) => {
    if (field.condition) {
      const conditionalityArr = field.condition.split(' ');
      const conditionalAction = conditionalityArr.slice(0, 2).join(' ');
      const fieldVertex = graph.findOrCreateVertex({
        left: field.id,
        action: conditionalAction,
      });
      graph.importConditionString(
        fieldVertex,
        conditionalityArr.slice(2).join(' '),
        stateToFieldIdMap,
      );
      initialState.form.fields[field.id].condition = ''; // eslint-disable-line no-param-reassign
    } else {
      const fieldVertex = graph.findOrCreateVertex({
        left: field.id,
        action: 'render if',
      });
      graph.addPath([0, fieldVertex], fieldVertex);
    }
  });
  initialState.form.fieldConditionalityInfo.dehydratedGraph = graph.dehydrate();
  // Store credentialType in meta for use in middleware and field operations
  initialState.meta = initialState.meta || {};
  initialState.meta.credentialType = credentialType;
  return initialState;
}

export function generateLoadedUIStateForField(field) {
  const uiState = { editors: {} };
  const fieldParams = Object.keys(field);
  fieldParams.forEach((fieldParam) => {
    if (REQUIRED_FIELD_PARAMETERS.includes(fieldParam)) {
      uiState[fieldParam] = { required: true };
    }
    if (fieldParam === 'value') {
      uiState.value = { required: field.hidden };
    }
    if (fieldParam === 'helpText') {
      uiState.helpText = {
        hasHelpText: {},
        value: {},
      };
    }
  });

  switch (field.type) {
    case FIELD_TYPES.DROPDOWN:
      uiState.editors.dropdownConfig = {};
      uiState.editors.dropdownConfig.options = {
        ...createDefaultUIStateForType(field.type).dropdownConfig.options,
      };
      uiState.editors.dropdownConfig.defaultOption = { required: field.hidden };
      Object.keys(field.editors.dropdownConfig.options).forEach((key) => {
        uiState.editors.dropdownConfig.options[key] = {};
      });

      return uiState;
    case FIELD_TYPES.MULTIPICKLIST:
      uiState.editors.multipicklistConfig = {};
      uiState.editors.multipicklistConfig.options = {
        ...createDefaultUIStateForType(field.type).multipicklistConfig.options,
      };
      uiState.editors.multipicklistConfig.defaultOption = {
        required: field.hidden,
      };
      Object.keys(field.editors.multipicklistConfig.options).forEach((key) => {
        uiState.editors.multipicklistConfig.options[key] = {};
      });

      return uiState;
    case FIELD_TYPES.DEGREE_OFFERING:
      uiState.editors.degreeOfferingConfig = {
        degreeOfferingConfig: {
          programId: {
            required: true,
          },
          degreeOffering: {},
        },
      };
      return uiState;
    case FIELD_TYPES.OPTIN:
    case FIELD_TYPES.PICKLIST_VALUE_OPT_IN:
    case FIELD_TYPES.BOOLEAN_OPT_IN:
    case FIELD_TYPES.NON_GDPR_CONSENT:
      uiState.editors.optInData = {
        checkboxText: {},
        disclaimer: {},
        hasCheckbox: {},
        hasDisclaimer: {},
        optInValue: {
          required: true,
        },
        format: {},
        defaultChecked: {},
        defaultRadio: {},
      };
      return uiState;
    case FIELD_TYPES.RADIO_BUTTON:
      uiState.editors.radioButtonConfig = {
        defaultOption: field.hidden ? { required: true } : {},
        options: {},
        layout: {},
      };
      return uiState;
    case FIELD_TYPES.GDPR_CONSENT:
      uiState.editors.gdprConsentData = {
        retailOptIn: {
          text: {},
          channelData: {
            email: {
              include: {},
              text: {},
            },
            phone: {
              include: {},
              text: {},
            },
            sms: {
              include: {},
              text: {},
            },
          },
        },
        leadShareOptIn: {
          include: {},
          text: {},
          leadShareValue: {},
          channelData: {
            email: {
              include: {},
              text: {},
            },
            phone: {
              include: {},
              text: {},
            },
            sms: {
              include: {},
              text: {},
            },
          },
        },
        disclaimer: {
          include: {},
          text: {},
        },
      };
      return uiState;
    default:
      return uiState;
  }
}

/**
 * As of 3/27/19, Only used to prevent saving of forms that are still using the old opt-in fields. This function and its call in
 * formBuilder reducer be removed once all prod forms are updated to use the new consent fields.
 */
export function generateInvalidFieldData(fields) {
  const newInvalidFormData = { fields: {} };
  let hasInvalidFormData = false;
  Object.keys(fields).forEach((fieldId) => {
    const field = fields[fieldId];
    if (field.type === FIELD_TYPES.OPTIN) {
      newInvalidFormData.fields[fieldId] = {
        permanent: {
          // Required to be a nested object or else the field will not get a red border.
          error: {
            visible: false,
            text: 'The legacy Opt-In field is deprecated, it must be removed before the form can be saved. Use the "consent_non_gdpr", "consent_gdpr" or "lead_share_opt_in__c" fields.',
          },
        },
      };
      hasInvalidFormData = true;
    }
  });
  if (hasInvalidFormData) return newInvalidFormData;
  return {};
}

export function shouldIncludeImpliedConsent(
  gdprSetting,
  impliedConsent = false,
) {
  return (
    (gdprSetting === GDPR_SETTING.ALWAYS && impliedConsent) ||
    [GDPR_SETTING.ALWAYS_IMPLIED, GDPR_SETTING.PASS_CONSENT_FOR_ALL_USERS].includes(gdprSetting)
  );
}
